import { useNavigate } from '@tanstack/react-router';
import { Ban, Link2, MailX, Pause, Pencil, Play, RotateCcw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { JobDetail } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { ModuleGate } from '../../../../components/ModuleGate';
import { Textarea } from '../../../../components/Input';
import { Popover, usePopover } from '../../../../components/Popover';
import { errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { useJobLifecycle, useReferralLink, type LifecycleChange } from '../../api/publishing-api';
import styles from './hub.module.css';

const MIN_REASON = 3;
/** Wide enough for a sentence of reason without wrapping every few words. */
const REASON_POPOVER_WIDTH = 320;

/**
 * F5.15–F5.17 on JOB-10. Only the moves the server offers in `actions` are shown —
 * the §L25 machine for the roles this person holds over the job — and the server
 * checks again. Hold is reversible, so it just happens with an undo (CLAUDE.md §5a
 * rule 8); closing opens its own screen (OFR-05), which says what happens to everyone.
 */
export function JobStatusActions({ detail }: { readonly detail: JobDetail }): ReactNode {
  const navigate = useNavigate();
  const lifecycle = useJobLifecycle(detail.id);
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const can = (action: JobDetail['actions'][number]): boolean => detail.actions.includes(action);

  async function move(change: LifecycleChange, message: string, detailText?: string): Promise<JobDetail | null> {
    try {
      const moved = await lifecycle.mutateAsync({ version: detail.version, change });
      show({ tone: 'success', message, ...(detailText ? { detail: detailText } : {}) });
      return moved;
    } catch (cause) {
      show({ tone: 'danger', message: "The job's status didn't change", detail: errorMessage(cause) });
      return null;
    }
  }

  async function hold(reason: string): Promise<void> {
    try {
      const held = await lifecycle.mutateAsync({ version: detail.version, change: { kind: 'hold', reason } });
      showUndo('Job on hold — its postings are offline', () => {
        void lifecycle
          .mutateAsync({ version: held.version, change: { kind: 'resume' } })
          .catch((cause: unknown) => show({ tone: 'danger', message: "Couldn't undo the hold", detail: errorMessage(cause) }));
      });
    } catch (cause) {
      show({ tone: 'danger', message: "The job wasn't put on hold", detail: errorMessage(cause) });
    }
  }

  return (
    <div className={styles.statusActions}>
      {can('edit') && (
        <Button
          variant="quiet"
          icon={Pencil}
          onClick={() => void navigate({ to: '/jobs/$jobId/setup/$section', params: { jobId: detail.id, section: 'basics' } })}
        >
          Edit setup
        </Button>
      )}
      <ModuleGate module="career_site">
        <ReferButton detail={detail} />
      </ModuleGate>
      {can('hold') && <ReasonButton label="Put on hold" icon={Pause} prompt="Why is this job on hold?" isRequired onSubmit={(reason) => void hold(reason)} />}
      {can('resume') && (
        <Button variant="solid" icon={Play} onClick={() => void move({ kind: 'resume' }, 'Job resumed', 'Form links closed by the hold are open again.')}>
          Resume
        </Button>
      )}
      {can('reopen_to_candidates') && (
        <Button variant="solid" icon={Play} onClick={() => void move({ kind: 'reopen-to-candidates' }, 'Job is taking candidates again', 'Reopen its links under Application form, in the job setup.')}>
          Take candidates again
        </Button>
      )}
      {can('reopen') && (
        <ReasonButton
          label="Reopen"
          icon={RotateCcw}
          prompt="Why reopen it? (optional)"
          onSubmit={(reason) => void move({ kind: 'reopen', ...(reason ? { reason } : {}) }, 'Job reopened', 'When you are ready, choose "Take candidates again".')}
        />
      )}
      {can('close') && (
        <Button variant="danger" icon={Ban} onClick={() => void navigate({ to: '/jobs/$jobId/close', params: { jobId: detail.id } })}>
          Close job
        </Button>
      )}
      {detail.status === 'filled' && (
        <Button variant="outline" icon={MailX} onClick={() => void navigate({ to: '/jobs/$jobId/close', params: { jobId: detail.id } })}>
          Close out
        </Button>
      )}
    </div>
  );
}

/** A reason, asked for next to the button that needs it (CLAUDE.md §5: pop-ups anchor). */
function ReasonButton({
  label,
  icon,
  prompt,
  isRequired = false,
  onSubmit,
}: {
  readonly label: string;
  readonly icon: typeof Pause;
  readonly prompt: string;
  readonly isRequired?: boolean;
  readonly onSubmit: (reason: string) => void;
}): ReactNode {
  const { isOpen, anchor, triggerRef, open, close } = usePopover();
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const isReady = !isRequired || trimmed.length >= MIN_REASON;

  function submit(): void {
    if (!isReady) return;
    close();
    setReason('');
    onSubmit(trimmed);
  }

  return (
    <>
      <Button ref={triggerRef} variant="outline" icon={icon} onClick={open} aria-expanded={isOpen}>
        {label}
      </Button>
      {isOpen && (
        <Popover anchor={anchor} onClose={close} align="right" width={REASON_POPOVER_WIDTH} label={label}>
          <form
            className={styles.reasonForm}
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <Field label={prompt} isRequired={isRequired}>
              {({ inputId, describedBy }) => (
                <Textarea
                  id={inputId}
                  aria-describedby={describedBy}
                  value={reason}
                  maxLength={1000}
                  rows={3}
                  autoFocus
                  onChange={(event) => setReason(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                      event.preventDefault();
                      submit();
                    }
                  }}
                />
              )}
            </Field>
            <div className={styles.reasonActions}>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button variant="solid" type="submit" disabled={!isReady}>
                {label}
              </Button>
            </div>
          </form>
        </Popover>
      )}
    </>
  );
}

/** F6.05 from the job itself: the viewer's own link, copied in one click. */
function ReferButton({ detail }: { readonly detail: JobDetail }): ReactNode {
  const referral = useReferralLink();
  const show = useToasts((state) => state.show);
  if (detail.status !== 'open' || detail.isConfidential) return null;

  async function copy(): Promise<void> {
    try {
      const link = await referral.mutateAsync(detail.id);
      await navigator.clipboard.writeText(link.trackedUrl).catch(() => undefined);
      show({ tone: 'success', message: 'Your referral link is copied', detail: `Anyone who applies through ${link.trackedUrl} is marked as referred by you.` });
    } catch (cause) {
      show({ tone: 'danger', message: "Couldn't get your referral link", detail: errorMessage(cause) });
    }
  }

  return (
    <Button variant="ghost" icon={Link2} onClick={() => void copy()}>
      Refer someone
    </Button>
  );
}
